/**
 * Geolocation Service
 *
 * Enriches IP addresses with geographic data using ip-api.com
 * Free tier: 45 requests/minute, no API key required, plain HTTP only.
 */

import { getCountryDisplayName } from "./countries";
import { deliberateAbort } from "./deliberate-abort";
import type { EdgeGeolocation } from "./ip-utils";

/**
 * `RESOLVED` placed the IP in a country. `FAILED` covers every way the lookup
 * can come back empty (timeout, rate limit, an address ip-api does not know);
 * `PRIVATE_IP` was never sent.
 */
export type GeolocationLookupStatus = "RESOLVED" | "FAILED" | "PRIVATE_IP";

export interface GeolocationData {
  status: GeolocationLookupStatus;
  city: string | null;
  country: string | null;
  countryCode: string | null;
  region: string | null;
  regionName: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  isp: string | null;
  /** Proxy, VPN or Tor exit address. */
  isProxy: boolean | null;
  /** Hosting provider, colocation or data centre address. */
  isHosting: boolean | null;
}

interface IpApiResponse {
  status: string;
  country?: string;
  countryCode?: string;
  region?: string;
  regionName?: string;
  city?: string;
  lat?: number;
  lon?: number;
  timezone?: string;
  isp?: string;
  proxy?: boolean;
  hosting?: boolean;
  message?: string;
}

const IP_API_FIELDS =
  "status,message,country,countryCode,region,regionName,city,lat,lon,timezone,isp,proxy,hosting";

function emptyGeolocation(status: GeolocationLookupStatus): GeolocationData {
  return {
    status,
    city: null,
    country: null,
    countryCode: null,
    region: null,
    regionName: null,
    latitude: null,
    longitude: null,
    timezone: null,
    isp: null,
    isProxy: null,
    isHosting: null,
  };
}

/**
 * Fetches geolocation data for an IP address
 * Uses ip-api.com free tier with graceful degradation
 *
 * @param ip - IP address to lookup
 * @param timeout - Request timeout in milliseconds (default: 3000ms)
 * @returns Geolocation data, with `status` saying whether the lookup placed it
 */
export async function getGeolocation(
  ip: string,
  timeout: number = 3000,
): Promise<GeolocationData> {
  if (isPrivateIp(ip)) {
    console.log(`Skipping geolocation for private IP: ${ip}`);
    return emptyGeolocation("PRIVATE_IP");
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort(deliberateAbort("geolocation lookup deadline"));
    }, timeout);

    const response = await fetch(
      `http://ip-api.com/json/${ip}?fields=${IP_API_FIELDS}`,
      {
        signal: controller.signal,
        headers: {
          "User-Agent": "Terac-Interview-Platform/1.0",
        },
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn(
        `Geolocation API error: ${response.status} ${response.statusText}`,
      );
      return emptyGeolocation("FAILED");
    }

    const data: IpApiResponse = await response.json();

    if (data.status === "fail" || !data.countryCode) {
      console.warn(`Geolocation lookup failed for ${ip}: ${data.message}`);
      return emptyGeolocation("FAILED");
    }

    return {
      status: "RESOLVED",
      city: data.city ?? null,
      country: data.country ?? null,
      countryCode: data.countryCode,
      region: data.region ?? null,
      regionName: data.regionName ?? null,
      latitude: data.lat ?? null,
      longitude: data.lon ?? null,
      timezone: data.timezone ?? null,
      isp: data.isp ?? null,
      isProxy: data.proxy ?? null,
      isHosting: data.hosting ?? null,
    };
  } catch (error) {
    // Gracefully degrade - don't fail the request if geolocation fails
    if (error instanceof Error && error.name === "AbortError") {
      console.warn(`Geolocation lookup timeout for ${ip}`);
    } else {
      console.warn(`Geolocation lookup error for ${ip}:`, error);
    }
    return emptyGeolocation("FAILED");
  }
}

/**
 * Where the location came from. `EDGE` is Vercel's own placement of the
 * connecting IP and cannot be influenced by the client; `LOOKUP` is ip-api's
 * placement, used when the edge said nothing. `FAILED` had an address and
 * could not place it, `NO_IP` had no address at all.
 */
export type RequestGeoResolution = "EDGE" | "LOOKUP" | "FAILED" | "NO_IP";

export interface RequestGeolocation {
  resolution: RequestGeoResolution;
  /** ISO 3166-1 alpha-2. */
  countryCode: string | null;
  countryName: string | null;
  /** ISO 3166-2 subdivision code without the country prefix, e.g. "NY". */
  regionCode: string | null;
  regionName: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  isp: string | null;
  isProxy: boolean | null;
  isHosting: boolean | null;
}

/**
 * The location of one request: the edge's placement when there is one, ip-api's
 * otherwise. ip-api is asked either way, because only it reports the ISP and
 * whether the address is a VPN, proxy or data centre.
 *
 * ip-api's own location fields are kept only where it agrees with the edge on
 * the country: mixing a city from one database into a country from another
 * would describe a place neither of them said.
 */
export async function resolveRequestGeolocation(params: {
  ip: string | null;
  edge: EdgeGeolocation | null;
}): Promise<RequestGeolocation> {
  const { ip, edge } = params;
  const lookup = ip ? await getGeolocation(ip) : null;
  const connection = {
    isp: lookup?.isp ?? null,
    isProxy: lookup?.isProxy ?? null,
    isHosting: lookup?.isHosting ?? null,
  };

  if (edge) {
    const lookupAgrees = lookup?.countryCode === edge.countryCode;
    return {
      resolution: "EDGE",
      countryCode: edge.countryCode,
      countryName:
        (lookupAgrees ? lookup?.country : null) ??
        getCountryDisplayName(edge.countryCode),
      regionCode: edge.regionCode,
      regionName:
        lookupAgrees && lookup?.region === edge.regionCode
          ? (lookup?.regionName ?? null)
          : null,
      city: edge.city,
      latitude: lookupAgrees ? (lookup?.latitude ?? null) : null,
      longitude: lookupAgrees ? (lookup?.longitude ?? null) : null,
      ...connection,
    };
  }

  if (lookup?.status === "RESOLVED") {
    return {
      resolution: "LOOKUP",
      countryCode: lookup.countryCode,
      countryName: lookup.country,
      regionCode: lookup.region,
      regionName: lookup.regionName,
      city: lookup.city,
      latitude: lookup.latitude,
      longitude: lookup.longitude,
      ...connection,
    };
  }

  return {
    resolution: ip ? "FAILED" : "NO_IP",
    countryCode: null,
    countryName: null,
    regionCode: null,
    regionName: null,
    city: null,
    latitude: null,
    longitude: null,
    ...connection,
  };
}

/**
 * Checks if an IP is in a private range
 */
function isPrivateIp(ip: string): boolean {
  const privateRanges = [
    /^10\./,
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
    /^192\.168\./,
    /^127\./,
    /^169\.254\./,
    /^::1$/,
    /^fc00:/,
    /^fe80:/,
  ];

  return privateRanges.some((range) => range.test(ip));
}
